import { findFacilityProcedureGroups } from './find-facility-procedure-groups.service';

import { SearchApiPartnerProcedureGroup } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { getLogger } from '@/logger/logger';

export { MAX_PROCEDURE_IDS } from './find-facility-procedure-groups.service';

export interface GetLaboratoryScheduleParams {
  facilityId: string;
  procedureIds: string[];
}

export interface GetLaboratoryScheduleResult {
  scheduleId: string | null;
  partnerId: string | null;
}

interface FacilityScheduleCandidates {
  all: string[];
  laboratorial: string[];
}

function scheduleCandidatesForFacility(
  group: SearchApiPartnerProcedureGroup,
  facilityId: string,
): FacilityScheduleCandidates {
  const all: string[] = [];
  const laboratorial: string[] = [];

  (group.options ?? [])
    .filter((option) => option.partnerFacilityId === facilityId)
    .forEach((option) => {
      const laboratorialSchedules = option.schedulesByScheduleType?.laboratorial ?? [];
      const arrivalOrderSchedules = option.schedulesByScheduleType?.arrivalOrder ?? [];

      laboratorialSchedules
        .filter((schedule) => Boolean(schedule.id))
        .forEach((schedule) => {
          all.push(schedule.id);
          laboratorial.push(schedule.id);
        });

      arrivalOrderSchedules
        .filter((schedule) => Boolean(schedule.id))
        .forEach((schedule) => all.push(schedule.id));
    });

  return { all: [...new Set(all)], laboratorial: [...new Set(laboratorial)] };
}

function intersect(current: string[] | null, incoming: string[]): string[] {
  if (current === null) {
    return incoming;
  }

  const incomingIds = new Set(incoming);

  return current.filter((id) => incomingIds.has(id));
}

export async function getLaboratoryScheduleFromSearchApi({
  facilityId,
  procedureIds,
}: GetLaboratoryScheduleParams): Promise<GetLaboratoryScheduleResult> {
  const logger = getLogger('laboratory.schedule');

  if (procedureIds.length === 0) {
    return { scheduleId: null, partnerId: null };
  }

  const { partnerId, groups, requestedProcedureIds } = await findFacilityProcedureGroups({
    facilityId,
    procedureIds,
  });

  if (!partnerId) {
    return { scheduleId: null, partnerId: null };
  }

  const returnedProcedureIds = new Set(groups.map((group) => group.procedure.id));
  const missingProcedureIds = requestedProcedureIds.filter((id) => !returnedProcedureIds.has(id));

  if (missingProcedureIds.length > 0) {
    logger.warn('No schedule group returned for every selected procedure', {
      laboratory: {
        event: 'incomplete_schedule_groups',
        facility_id: facilityId,
        missing_procedure_ids: missingProcedureIds,
      },
    });

    return { scheduleId: null, partnerId };
  }

  const resolved = groups.reduce<{ all: string[] | null; laboratorial: string[] | null }>(
    (accumulated, group) => {
      const candidates = scheduleCandidatesForFacility(group, facilityId);

      return {
        all: intersect(accumulated.all, candidates.all),
        laboratorial: intersect(accumulated.laboratorial, candidates.laboratorial),
      };
    },
    { all: null, laboratorial: null },
  );

  const shared = resolved.all ?? [];
  const sharedLaboratorial = resolved.laboratorial ?? [];
  const preferred = shared.find((id) => sharedLaboratorial.includes(id));

  return { scheduleId: preferred ?? shared[0] ?? null, partnerId };
}
